import { useEffect, useMemo, useRef, useState } from "react";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { Check, Info, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  type NotificationPreferences,
} from "@/hooks/useNotifications";
import { cn } from "@/lib/utils";
import { FormSection, SectionDivider, TabHeader } from "./profile-form";

// ── Preference catalog ────────────────────────────────────────────────────────
// Stable keys persisted as a JSON blob on the user (the backend stores whatever
// booleans it's sent). Defaults below apply when a key has never been saved.

interface PrefItem {
  key: string;
  title: MessageDescriptor;
  description: MessageDescriptor;
  default: boolean;
}

interface PrefSection {
  id: string;
  title: MessageDescriptor;
  description: MessageDescriptor;
  items: PrefItem[];
}

const CHANNELS: { key: string; label: MessageDescriptor; default: boolean }[] = [
  { key: "channels.email", label: msg`Email`, default: true },
  { key: "channels.in_app", label: msg`In-app`, default: true },
  { key: "channels.sms", label: msg`SMS`, default: false },
];

const SECTIONS: PrefSection[] = [
  {
    id: "audit",
    title: msg`Audit & admin actions`,
    description: msg`Notifications about sensitive administrative activity.`,
    items: [
      { key: "audit.company_changes", title: msg`Concept changes`, description: msg`Concept details, configuration, plan or billing profile updates.`, default: true },
      { key: "audit.member_management", title: msg`Member management`, description: msg`Invites, role assignments and account removals.`, default: false },
      { key: "audit.security", title: msg`Security alerts`, description: msg`New sign-ins, password resets and 2FA changes.`, default: true },
      { key: "audit.data_exports", title: msg`Data exports`, description: msg`When an export or report you requested completes.`, default: false },
      { key: "audit.api_tokens", title: msg`API tokens`, description: msg`Tokens created, revoked or nearing expiry.`, default: false },
    ],
  },
  {
    id: "orders",
    title: msg`Orders & kitchen`,
    description: msg`Updates from the live order board.`,
    items: [
      { key: "orders.new", title: msg`New orders`, description: msg`A new order is sent to the kitchen.`, default: true },
      { key: "orders.assignments", title: msg`Assigned to you`, description: msg`An order or cooking session is assigned to you.`, default: true },
      { key: "orders.status", title: msg`Status changes`, description: msg`An order moves between stages or is ready to plate.`, default: false },
      { key: "orders.late", title: msg`Late orders`, description: msg`A cooking session exceeds its target time.`, default: true },
      { key: "orders.allergen", title: msg`Allergen alerts`, description: msg`An order is flagged for allergens.`, default: true },
    ],
  },
  {
    id: "inventory",
    title: msg`Inventory`,
    description: msg`Stock levels at your locations.`,
    items: [
      { key: "inventory.low_stock", title: msg`Low / out of stock`, description: msg`An item drops to or below its reorder level.`, default: true },
      { key: "inventory.restock", title: msg`Restocked`, description: msg`An item is restocked at a location.`, default: false },
    ],
  },
  {
    id: "recipes",
    title: msg`Recipes & menu`,
    description: msg`Changes to recipes and menus.`,
    items: [
      { key: "recipes.published", title: msg`Recipe published`, description: msg`A recipe is published or archived.`, default: true },
      { key: "recipes.edited", title: msg`Recipe edited`, description: msg`Someone updates a recipe you own.`, default: false },
      { key: "recipes.comments", title: msg`Comments`, description: msg`A teammate comments on your recipe.`, default: false },
    ],
  },
  {
    id: "account",
    title: msg`Account`,
    description: msg`Personal account notifications.`,
    items: [
      { key: "account.new_device", title: msg`New device sign-in`, description: msg`We notice a sign-in from a new device.`, default: true },
      { key: "account.weekly_summary", title: msg`Weekly summary`, description: msg`A weekly digest of your activity.`, default: false },
    ],
  },
];

function buildDefaults(): NotificationPreferences {
  const defaults: NotificationPreferences = {};
  for (const channel of CHANNELS) defaults[channel.key] = channel.default;
  for (const section of SECTIONS) {
    for (const item of section.items) defaults[item.key] = item.default;
  }
  return defaults;
}

function ChannelPill({
  label,
  selected,
  onToggle,
}: {
  label: string;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium",
        selected
          ? "bg-(--color-action) text-(--color-action-fg)"
          : "bg-(--color-surface-muted) text-(--color-text-secondary)",
      )}
    >
      {selected ? <Check className="size-4" /> : null}
      {label}
    </button>
  );
}

function CheckRow({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <Checkbox
        checked={checked}
        onCheckedChange={(value) => onChange(value === true)}
      />
      <div>
        <div className="text-sm font-medium text-(--color-text-primary)">
          {title}
        </div>
        <div className="text-sm text-(--color-text-secondary)">
          {description}
        </div>
      </div>
    </label>
  );
}

export function NotificationsTab() {
  const { t } = useLingui();
  const defaults = useMemo(() => buildDefaults(), []);
  const { data: saved } = useNotificationPreferences();
  const updatePrefs = useUpdateNotificationPreferences();

  const [draft, setDraft] = useState<NotificationPreferences>(defaults);
  const hydrated = useRef(false);

  // Hydrate the form once the saved preferences arrive (defaults fill any gaps).
  useEffect(() => {
    if (saved && !hydrated.current) {
      hydrated.current = true;
      setDraft({ ...defaults, ...saved });
    }
  }, [saved, defaults]);

  const valueOf = (key: string) => draft[key] ?? defaults[key] ?? false;
  const set = (key: string, value: boolean) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const handleSave = () =>
    updatePrefs.mutate(draft, {
      onSuccess: () => toast.success(t`Notification preferences saved`),
      onError: (error: unknown) =>
        toast.error(
          error instanceof Error ? error.message : t`Failed to save preferences`,
        ),
    });

  return (
    <div className="flex flex-col gap-8">
      <TabHeader
        title={t`Notifications`}
        description={t`Control how you receive updates across Yes, Chef.`}
        actions={
          <>
            <Button variant="outline" onClick={() => setDraft({ ...defaults })}>
              <RotateCcw />
              <Trans>Reset to defaults</Trans>
            </Button>
            <Button onClick={handleSave} disabled={updatePrefs.isPending}>
              {updatePrefs.isPending ? t`Saving…` : t`Save`}
            </Button>
          </>
        }
      />

      <div className="flex items-start gap-3 rounded-xl bg-(--color-surface-muted) p-4">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-(--color-action) text-(--color-action-fg)">
          <Info className="size-5" />
        </div>
        <div>
          <p className="text-sm font-medium text-(--color-text-primary)">
            <Trans>System message</Trans>
          </p>
          <p className="text-sm text-(--color-text-secondary)">
            <Trans>
              Critical alerts (security, password resets) are always delivered
              in-app. Email depends on your settings.
            </Trans>
          </p>
        </div>
      </div>

      <SectionDivider />

      <FormSection
        title={t`Default channels`}
        description={t`Defaults applied to every new notification type.`}
      >
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-sm font-medium text-(--color-text-primary)">
              <Trans>Defaults for new notification types.</Trans>
            </p>
            <p className="text-sm text-(--color-text-secondary)">
              <Trans>Individual categories can override these.</Trans>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {CHANNELS.map((channel) => (
              <ChannelPill
                key={channel.key}
                label={t(channel.label)}
                selected={valueOf(channel.key)}
                onToggle={() => set(channel.key, !valueOf(channel.key))}
              />
            ))}
          </div>
        </div>
      </FormSection>

      {SECTIONS.map((section) => (
        <div key={section.id} className="flex flex-col gap-8">
          <SectionDivider />
          <FormSection
            title={t(section.title)}
            description={t(section.description)}
          >
            <div className="flex flex-col gap-5">
              {section.items.map((item) => (
                <CheckRow
                  key={item.key}
                  title={t(item.title)}
                  description={t(item.description)}
                  checked={valueOf(item.key)}
                  onChange={(value) => set(item.key, value)}
                />
              ))}
            </div>
          </FormSection>
        </div>
      ))}
    </div>
  );
}
